import { imageSource, imageSourceSet } from "../lib/responsiveImage";
import { LinkPreview } from "./LinkPreview";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Image as ImageIcon, Maximize2, X } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { slug } from "../lib/format";
import { useDialogFocus } from "../lib/useDialogFocus";
import { MarginNoteCard } from "./MarginNotes";
import type { MarginNote, StoryIllustration, SourceLink } from "../types";
import { BrandMark } from "./BrandMark";

function InlineText({ children, sources }: { children: string; sources?: SourceLink[] }) {
  const tokens = children.split(/(\*\*.+?\*\*|\*[^*\n]+?\*|\[[^\]]+\]\([^)]+\))/g);
  return <>{tokens.map((token, index) => {
    const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return <LinkPreview key={index} url={link[2]} title={sources?.find(source => source.url === link[2])?.title || link[1]}>{link[1]}</LinkPreview>;
    if (token.startsWith("**") && token.endsWith("**")) return <strong key={index}>{token.slice(2, -2)}</strong>;
    if (token.length >= 3 && token.startsWith("*") && token.endsWith("*")) return <em key={index}>{token.slice(1, -1)}</em>;
    return <Fragment key={index}>{token}</Fragment>;
  })}</>;
}

const EMPTY_ILLUSTRATIONS: StoryIllustration[] = [];

const EMPTY_NOTES: MarginNote[] = [];

/** Текст истории c иллюстрациями и заметками на полях под теми абзацами, к которым их задавали. */
export function StoryBody({ content, illustrations, notes, sources }: { sources?: SourceLink[]; content: string; illustrations?: StoryIllustration[]; notes?: MarginNote[] }) {
  const reduceMotion = useReducedMotion();
  const [activeId, setActiveId] = useState<string | null>(null);
  const orderedIllustrations = useMemo(
    () => [...(illustrations || EMPTY_ILLUSTRATIONS)].sort((first, second) => first.index - second.index),
    [illustrations],
  );
  const activeIndex = activeId ? orderedIllustrations.findIndex((item) => item.id === activeId) : -1;
  const activeIllustration = activeIndex >= 0 ? orderedIllustrations[activeIndex] : null;
  const lightboxRef = useDialogFocus<HTMLDivElement>(Boolean(activeIllustration), () => setActiveId(null));

  function moveIllustration(offset: number) {
    if (activeIndex < 0 || orderedIllustrations.length < 2) return;
    const nextIndex = (activeIndex + offset + orderedIllustrations.length) % orderedIllustrations.length;
    setActiveId(orderedIllustrations[nextIndex].id);
  }

  useEffect(() => {
    if (!activeIllustration) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") moveIllustration(-1);
      if (event.key === "ArrowRight") moveIllustration(1);
    };
    window.addEventListener("keydown", handleKeyDown);
    if (orderedIllustrations.length > 1) {
      const previous = orderedIllustrations[(activeIndex - 1 + orderedIllustrations.length) % orderedIllustrations.length];
      const next = orderedIllustrations[(activeIndex + 1) % orderedIllustrations.length];
      [previous, next].forEach((item) => { const image = new window.Image(); image.src = item.url; });
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeId, activeIndex, activeIllustration, orderedIllustrations]);

  const lightbox = typeof document === "undefined" ? null : createPortal(
    <AnimatePresence>
      {activeIllustration && <motion.div
        ref={lightboxRef}
        className="illustration-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={`Иллюстрация ${activeIndex + 1} из ${orderedIllustrations.length}`}
        tabIndex={-1}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.28 }}
        onMouseDown={(event) => { if (event.target === event.currentTarget) setActiveId(null); }}
      >
        <motion.header initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: reduceMotion ? 0 : 0.16 }}>
          <div className="lightbox-brand"><span><BrandMark compact /></span><div><small>Because.FM</small><b>Иллюстрация к истории</b></div></div>
          <div className="lightbox-counter" aria-live="polite"><b>{String(activeIndex + 1).padStart(2, "0")}</b><span>/</span>{String(orderedIllustrations.length).padStart(2, "0")}</div>
          <button type="button" className="lightbox-close" onClick={() => setActiveId(null)} aria-label="Закрыть полноэкранный просмотр" autoFocus><X /></button>
        </motion.header>

        <div className="lightbox-stage" onMouseDown={(event) => { if (event.target === event.currentTarget) setActiveId(null); }}>
          {orderedIllustrations.length > 1 && <button type="button" className="lightbox-nav lightbox-previous" onClick={() => moveIllustration(-1)} aria-label="Предыдущая иллюстрация"><ChevronLeft /></button>}
          <AnimatePresence mode="wait" initial={false}>
            <motion.figure
              className="lightbox-figure"
              key={activeIllustration.id}
              initial={reduceMotion ? false : { opacity: 0, x: 28, scale: 0.985 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -28, scale: 0.985 }}
              transition={{ duration: reduceMotion ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
              drag={!reduceMotion && orderedIllustrations.length > 1 ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.12}
              onDragEnd={(_, info) => {
                if (info.offset.x < -70) moveIllustration(1);
                if (info.offset.x > 70) moveIllustration(-1);
              }}
            >
              <motion.img layoutId={`story-illustration-${activeIllustration.id}`} src={activeIllustration.url} alt={activeIllustration.alt} draggable={false} />
            </motion.figure>
          </AnimatePresence>
          {orderedIllustrations.length > 1 && <button type="button" className="lightbox-nav lightbox-next" onClick={() => moveIllustration(1)} aria-label="Следующая иллюстрация"><ChevronRight /></button>}
        </div>

        <motion.footer initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: reduceMotion ? 0 : 0.18 }}>
          <div className="lightbox-caption"><ImageIcon /><div><small>Сцена {String(activeIllustration.index).padStart(2, "0")}</small><p>{activeIllustration.alt}</p></div></div>
          {orderedIllustrations.length > 1 && <div className="lightbox-thumbnails" aria-label="Все иллюстрации">{orderedIllustrations.map((item, index) => <button
            type="button"
            key={item.id}
            className={item.id === activeIllustration.id ? "active" : ""}
            onClick={() => setActiveId(item.id)}
            aria-label={`Открыть иллюстрацию ${index + 1}`}
            aria-current={item.id === activeIllustration.id ? "true" : undefined}
          ><img src={imageSource(item.url, 320)} alt="" loading="lazy" /><span>{String(index + 1).padStart(2, "0")}</span></button>)}</div>}
          <span className="lightbox-hint">← → листать · Esc закрыть</span>
        </motion.footer>
      </motion.div>}
    </AnimatePresence>,
    document.body,
  );

  const storyNotes = notes || EMPTY_NOTES;
  const blocks = content.split(/\n\s*\n/).filter(Boolean);
  // Заметку цепляем к абзацу, в котором нашлась её цитата. Не нашлась (текст
  // правился, цитата пришла из другого места) — показываем в конце, но не теряем.
  const noteIndexFor = (note: MarginNote) => blocks.findIndex((block) => block.replace(/\s+/g, " ").includes(note.quote));
  const notesByBlock = new Map<number, MarginNote[]>();
  const orphanNotes: MarginNote[] = [];
  for (const note of storyNotes) {
    const at = noteIndexFor(note);
    if (at < 0) { orphanNotes.push(note); continue; }
    notesByBlock.set(at, [...(notesByBlock.get(at) || []), note]);
  }
  const withNotes = (index: number, node: React.ReactNode) => {
    const attached = notesByBlock.get(index);
    if (!attached?.length) return node;
    return <Fragment key={`block-${index}`}>{node}{attached.map((note) => <MarginNoteCard key={note.id} note={note} />)}</Fragment>;
  };

  return <><div className="story-body">{blocks.map((block, index) => {
    const text = block.trim();
    const marker = text.match(/^\[\[ILLUSTRATION_(\d+)\]\]$/);
    if (marker) {
      const illustration = orderedIllustrations.find((item) => item.index === Number(marker[1]));
      return illustration ? <figure className="story-illustration" key={`illustration-${illustration.id}`}><motion.button
        type="button"
        className="story-illustration-preview"
        onClick={() => setActiveId(illustration.id)}
        aria-label={`Открыть иллюстрацию ${illustration.index} на весь экран`}
        whileTap={reduceMotion ? undefined : { scale: 0.99 }}
      ><motion.img layoutId={`story-illustration-${illustration.id}`} src={imageSource(illustration.url, 960)} srcSet={imageSourceSet(illustration.url)} sizes="(max-width: 820px) calc(100vw - 64px), 754px" alt={illustration.alt} loading="lazy" /><span className="story-illustration-open"><Maximize2 /><em>На весь экран</em></span></motion.button><figcaption><span><ImageIcon /> Иллюстрация {String(illustration.index).padStart(2, "0")} · создана для этой сцены</span><span>Нажмите, чтобы рассмотреть</span></figcaption></figure> : null;
    }
    if (text.startsWith("# ")) return null;
    if (text.startsWith("## ")) { const heading = text.slice(3).trim(); return withNotes(index, <h2 id={slug(heading)} key={index}>{heading}</h2>); }
    if (text.startsWith("### ")) return withNotes(index, <h3 key={index}>{text.slice(4)}</h3>);
    if (text.startsWith("> ")) return withNotes(index, <blockquote key={index}><InlineText sources={sources}>{text.slice(2)}</InlineText></blockquote>);
    if (text.split("\n").every((line) => line.startsWith("- "))) return withNotes(index, <ul key={index}>{text.split("\n").map((line) => <li key={line}><InlineText sources={sources}>{line.slice(2)}</InlineText></li>)}</ul>);
    return withNotes(index, <p key={index}><InlineText sources={sources}>{text.replace(/\n/g, " ")}</InlineText></p>);
  })}{orphanNotes.map((note) => <MarginNoteCard key={note.id} note={note} />)}</div>{lightbox}</>;
}
